'use client';
/**
 * header地址信息展示组件
 * 主要功能:
 * 显示当前配送地址邮编
 * 提供地址选择抽屉入口
 * 根据地理位置检测显示国家切换提示
 */

// React
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
// 三方库/框架
import { useTranslation } from '@/i18n/client';
import { observer } from 'mobx-react-lite';
import { usePathname } from 'next/navigation';
import CloseIcon from '@mui/icons-material/Close';
// 公共方法
import { EXPOSURE_RECORD, sendClickTrack } from '@/tracks/25061';
import { getGeolocationCountryCode, isCountryDefaultAddress } from '@/utils/addressUtils';
import { userSwitchCountry } from '@/utils/countryUtils';
import { saveUrlExtraParams } from '@/utils/extUrlParamsUtils';
import { COUNTRY_ACCESS_WHITE_LIST } from '@/constants/address';
// Store
import { useStores } from '@/models';
// 组件
import UKLoadingDots from '@/components/UKLoadingDots';
import { Button, ClickAwayListener, IconButton, Paper, Popper } from '@mui/material';
import UKToast from '@/components/UKToast';

import { GeolocationSourceType } from '@/apis/address/address.interface';
// 资源
import CoordinateIcon from '@/assets/icons/address/CoordinateIcon.svg';
import ArrowDown from '@/assets/icons/arrow-down.svg';
import DeliverSVG from '@/assets/icons/deliver.svg';

import { COUNTRY_ALERT_DISMISSED, ADDRESS_ALERT_DISMISSED } from '@/constants/address';
// 样式
import styles from './index.module.scss';

interface Props {
  serverPostCode: string;
}

const AddressSelectDrawer = lazy(
  () => import('@/components/address/AddressSelect/AddressSelectDrawer'),
);

const AddressButton = observer(({ serverPostCode }: Props) => {
  const { t } = useTranslation();
  const pathname = usePathname();
  const { countryStore } = useStores();

  const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false); // 控制地址选择抽屉的显示状态
  const [showAddressPopper, setShowAddressPopper] = useState<boolean>(false); // 控制地址提示弹层的显示状态

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [currentCountryName, setCurrentCountryName] = useState<string>('');
  const [deviceCountryCode, setDeviceCountryCode] = useState<string>('');
  const [targetCountryName, setTargetCountryName] = useState<string>('');
  const [geoType, setGeoType] = useState<GeolocationSourceType>('ip');
  const [alertType, setAlertType] = useState<'country' | 'address' | ''>('');

  useEffect(() => {
    saveUrlExtraParams(); // 保存URL中的额外参数

    // 设置Popper锚点
    if (containerRef.current) {
      setAnchorEl(containerRef.current);
    }
  }, []);

  /**
   * 地址信息更新时，检查是否需要显示国家切换提示
   */
  useEffect(() => {
    if (countryStore.currentAddress) {
      handleCountryAlert();
    }
  }, [countryStore.currentAddress]);

  /**
   * 当前邮编显示文本
   */
  const postCode = useMemo(() => {
    return countryStore?.currentAddress?.postCode
      ? `${t('common.deliverTo')} ${countryStore.currentAddress.postCode}`
      : '';
  }, [t, countryStore.currentAddress]);

  /**
   * 根据用户地理位置和当前选择的国家判断是否需要显示切换国家的提示
   */
  const handleCountryAlert = async () => {
    const { countryList, currentAddress, currentCountryCode } = countryStore;

    const isHomePage = pathname === '/';

    // 检查用户是否点击过国家提示dismiss按钮
    const isCountryAlertDismissed = localStorage.getItem(COUNTRY_ALERT_DISMISSED) === 'true';

    // 获取设备地理位置对应的国家代码
    const { countryCode: deviceCountryCode, sourceType } = await getGeolocationCountryCode();

    // 国家提示 - 优先级最高
    // 可切换国家 && 未关闭提示 && 当前国家与设备地理位置国家不一致
    if (
      isHomePage &&
      countryList?.length > 1 &&
      !isCountryAlertDismissed &&
      currentCountryCode &&
      deviceCountryCode &&
      currentCountryCode !== deviceCountryCode
    ) {
      const currentTranslationKey = `common.countryName.${currentCountryCode}`;
      const currentCountryName = t(currentTranslationKey);

      const deviceTranslationKey = `common.countryName.${deviceCountryCode}`;
      const deviceCountryName = t(deviceTranslationKey);

      if (sourceType === 'webApi' && deviceCountryName !== deviceTranslationKey) {
        // 精确定位 && 设备所在国家在当前支持站点范围 使用精确提示 直接导航至目标国家
        setGeoType('webApi');
        setDeviceCountryCode(deviceCountryCode);
        setTargetCountryName(deviceCountryName);
        setShowAddressPopper(true);
        setAlertType('country');
        return;
      } else if (currentCountryName && currentCountryName !== currentTranslationKey) {
        // 使用模糊提示 引导用户自己切换国家
        setGeoType('ip');
        setCurrentCountryName(currentCountryName);
        setShowAddressPopper(true);
        setAlertType('country');
        return;
      }
    }

    // 如果当前路由在白名单中，不显示地址提示
    if (COUNTRY_ACCESS_WHITE_LIST.includes(pathname)) {
      return;
    }

    const isDefaultAddressFlag = isCountryDefaultAddress({
      address: currentAddress,
      countryCode: currentCountryCode,
    });

    // 检查用户是否点击过地址提示dismiss按钮
    const isAddressAlertDismissed = localStorage.getItem(ADDRESS_ALERT_DISMISSED) === 'true';

    // 地址提示 - 次优先级
    if (isDefaultAddressFlag && !isAddressAlertDismissed) {
      setShowAddressPopper(true);
      setAlertType('address');
    }
  };

  /**
   * 打开地址选择抽屉并关闭提示弹层
   */
  const handlerDeliver = () => {
    setIsOpenDialog(true);
    setShowAddressPopper(false);
  };

  const handlerPopperConfirm = () => {
    if (geoType === 'webApi') {
      userSwitchCountry({
        countryCode: deviceCountryCode,
      });
      UKToast.show({
        content: t('address.countrySwitchSuccess'),
        icon: 'success',
        duration: 2000,
      });
    } else {
      setIsOpenDialog(true);
      setShowAddressPopper(false);
    }
  };

  /**
   * dismiss按钮事件
   */
  const handlePopperDismiss = () => {
    setShowAddressPopper(false); // 关闭提示弹层
    localStorage.setItem(COUNTRY_ALERT_DISMISSED, 'true'); // 记录用户已关闭提示
  };

  const handleAddressPopperDismiss = () => {
    setShowAddressPopper(false); // 关闭提示弹层
    localStorage.setItem(ADDRESS_ALERT_DISMISSED, 'true'); // 记录用户已关闭提示
  };

  return (
    <div
      className={styles.deliver}
      data-exptag={"exp|" + EXPOSURE_RECORD.Header_Location_Expo}
      data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
      onClick={() => {
        sendClickTrack.Header_Location();
      }}
      ref={containerRef}
    >
      <button onClick={handlerDeliver} className={styles.deliverWrap}>
        <div>
          <DeliverSVG className={styles.deliverIcon} />
        </div>
        {postCode || serverPostCode ? (
          <>{postCode || serverPostCode}</>
        ) : (
          <UKLoadingDots className={styles.loadingWrap} />
        )}
        <div>
          <ArrowDown className={styles.deliverArrow} />
        </div>
      </button>

      {showAddressPopper && (
        <ClickAwayListener
          onClickAway={() => {
            if (alertType === 'country') {
              // 国家提示弹窗允许点击外部区域关闭
              setShowAddressPopper(false);
            }
          }}
        >
          <Popper
            className={styles.popperContainer}
            open={showAddressPopper}
            anchorEl={anchorEl}
            placement='bottom-end'
            disablePortal={true}
            sx={{
              '&[data-popper-placement*="bottom"] .MuiPaper-root::before': {
                content: '""',
                position: 'absolute',
                top: '-7px',
                right: '15%',
                width: '14px',
                height: '14px',
                backgroundColor: '#FFFFFF',
                transform: 'rotate(45deg)',
                boxShadow: '-2px -2px 5px rgba(0, 0, 0, 0.1)',
                borderRadius: '2px',
              },
            }}
          >
            {alertType === 'country' && (
              <Paper elevation={3} className={styles.popperPaper}>
                <div className={styles.popperContent}>
                  <div>
                    <CoordinateIcon />
                  </div>
                  <div className={styles.alertText}>
                    {geoType === 'webApi' ? (
                      <span>
                        {t('address.countrySwitchAlert', { countryName: targetCountryName })}
                      </span>
                    ) : (
                      <span>{t('address.headerAlert', { countryName: currentCountryName })}</span>
                    )}
                  </div>
                </div>

                <div className={styles.popperFooter}>
                  <Button variant='outlined' size='small' onClick={handlePopperDismiss}>
                    {t('common.btnText.dismiss')}
                  </Button>

                  <Button variant='contained' size='small' onClick={handlerPopperConfirm}>
                    {geoType === 'webApi' ? (
                      <span>{t('common.btnText.confirm')}</span>
                    ) : (
                      <span>{t('common.btnText.changeAddress')}</span>
                    )}
                  </Button>
                </div>
              </Paper>
            )}

            {alertType === 'address' && (
              <Paper elevation={3} className={styles.popperPaper}>
                <div className={styles.addressPopperContent}>
                  <div className={styles.alertText}>{t('address.selectAddressAlert')}</div>

                  <div className={styles.popperFooter}>
                    <Button variant='outlined' size='small' onClick={handleAddressPopperDismiss}>
                      {t('common.btnText.gotIt')}
                    </Button>
                  </div>
                </div>
              </Paper>
            )}
          </Popper>
        </ClickAwayListener>
      )}

      <Suspense fallback={null}>
        <AddressSelectDrawer openDrawer={isOpenDialog} setOpenDrawer={setIsOpenDialog} />
      </Suspense>
    </div>
  );
});

export default AddressButton;
